import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import type { CreateConversationInput, SendMessageInput } from '@/lib/marina/validators'

export type ConversationKind = 'DIRECT' | 'GROUP' | 'SUPPORT'

export interface ChatUser {
  id: string
  name: string
  email: string
}

export interface ChatParticipant {
  id: string
  userId: string
  user: ChatUser
  joinedAt: string
  lastReadAt: string | null
}

export interface ChatMessage {
  id: string
  conversationId: string
  senderUserId: string
  sender?: ChatUser
  body: string
  attachments?: { url: string; filename: string; size: number; mime: string }[] | null
  createdAt: string
  editedAt: string | null
  deletedAt: string | null
}

export interface Conversation {
  id: string
  kind: ConversationKind
  title: string | null
  updatedAt: string
  participants: ChatParticipant[]
  lastMessage: ChatMessage | null
  unreadCount: number
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useConversations() {
  return useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => apiFetch<{ items: Conversation[] }>('/api/chat/conversations'),
    staleTime: 10_000,
  })
}

export function useConversation(id: string | null | undefined) {
  return useQuery({
    queryKey: ['chat', 'conversation', id],
    queryFn: () => apiFetch<{ conversation: Conversation }>(`/api/chat/conversations/${id}`),
    enabled: !!id,
  })
}

export function useMessages(conversationId: string | null | undefined) {
  return useQuery({
    queryKey: ['chat', 'messages', conversationId],
    queryFn: () => apiFetch<{ messages: ChatMessage[] }>(`/api/chat/conversations/${conversationId}/messages`),
    enabled: !!conversationId,
    staleTime: 5_000,
  })
}

export function useCreateConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateConversationInput) =>
      jsonFetch<{ conversation: Conversation }>('/api/chat/conversations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
  })
}

export function useSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: SendMessageInput }) =>
      jsonFetch<{ message: ChatMessage }>(`/api/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['chat', 'messages', v.conversationId] })
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
    },
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/chat/conversations/${conversationId}/mark-read`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
  })
}

/**
 * Single WebSocket for all chat conversations. Call subscribe(conversationId)
 * to join a channel; handler receives broadcast messages.
 */
export function useChatWebSocket(onMessage: (msg: any) => void) {
  const wsRef = useRef<WebSocket | null>(null)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/chat`)
    wsRef.current = ws

    ws.addEventListener('message', (e) => {
      try {
        const msg = JSON.parse(e.data)
        onMessageRef.current(msg)
      } catch {
        /* ignore */
      }
    })

    return () => {
      ws.close()
      wsRef.current = null
    }
  }, [])

  const subscribe = useCallback((conversationId: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'subscribe', conversationId }))
  }, [])

  const unsubscribe = useCallback((conversationId: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'unsubscribe', conversationId }))
  }, [])

  return { subscribe, unsubscribe }
}
